import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import moment from 'moment';

import { legShape } from 'constants/propTypes';
import { passengerTypes } from 'types/passengers';
import { colours } from 'themes/default';
import currencySymbols from 'constants/currencies';

import { AFTERSALES_RULES, TRANSLATION_ID, DATE_FORMAT, TIME_FORMAT } from 'constants/fareConditions';

import {
  shouldDisplayNotPossibleFareConditionsMessage,
  buildFareConditionItems,
  areAllAfterSalesChecksFalseForGivenRule,
  displayAfterSalesNotPossibleMessage
} from './utils';

import { FareWrapper, FareConditionsInactiveMessage, FareContainer, FareDescription, StyledTable } from './FareConditions.styles';

const FareConditions = ({ journey, currency = 'GBP', intl, selectedProduct }) => {
  const bookingItems =
    journey?.bookingItems?.filter((e) => e.productFamily?.familyGroupName === selectedProduct) || journey?.bookingItems || [];
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fareConditionItems = buildFareConditionItems({ bookingItems });

  const columns = [
    { key: intl.formatMessage({ id: `${TRANSLATION_ID}.afterSales` }) },
    ...[...fareConditionItems.types].map((key) => ({ key: intl.formatMessage({ id: `passengerType.${passengerTypes[key]}` }), type: key }))
  ];

  const rows = Object.keys(AFTERSALES_RULES).map((afterSalesKey) => {
    const row = {};
    row.id = afterSalesKey;

    columns.forEach(({ key: columnKey, type }) => {
      let aftersalesConditionsToDisplay = null;

      if (type === undefined) {
        aftersalesConditionsToDisplay = intl.formatMessage({ id: `${TRANSLATION_ID}.${AFTERSALES_RULES[afterSalesKey]}` });
      } else if (fareConditionItems.details?.length > 0) {
        const allAfterSalesChecksFalseForGivenRule = areAllAfterSalesChecksFalseForGivenRule({
          details: fareConditionItems.details,
          type,
          afterSalesKey
        });
        const fareConditionItem = fareConditionItems.details.find(({ passenger }) => passenger?.type === type);
        const fareConditionsAfterSalesRulesWithoutAOP = fareConditionItem?.afterSalesRules.filter(
          ({ type, override }) => type === afterSalesKey && !override
        );
        const sortedFareConditionsAfterSalesRules = fareConditionsAfterSalesRulesWithoutAOP.sort(
          (a, b) => new Date(a.validUntil).getTime() - new Date(b.validUntil).getTime()
        );

        aftersalesConditionsToDisplay =
          sortedFareConditionsAfterSalesRules.length === 0
            ? displayAfterSalesNotPossibleMessage({
                afterSalesKey,
                cannotBeCancelledAndRefunded: !fareConditionItem.canBeCancelled && !fareConditionItem.canBeRefunded,
                intl
              })
            : sortedFareConditionsAfterSalesRules.map(({ price, validUntil, active }) => {
                const date = moment(validUntil);

                return {
                  text: intl.formatMessage(
                    {
                      id: `${TRANSLATION_ID}.${AFTERSALES_RULES[afterSalesKey]}${price > 0 ? 'WithPrice' : 'WithOutPrice'}`
                    },
                    {
                      ...(price > 0 && { price: `${currencySymbols[currency]}${price}` }),
                      validUntil: `${date.format(DATE_FORMAT)} ${intl.formatMessage({ id: `${TRANSLATION_ID}.at` })} ${date.format(
                        TIME_FORMAT
                      )} ${localTimeZone}`
                    }
                  ),
                  active,
                  allAfterSalesChecksFalseForGivenRule,
                  canBeRefunded: fareConditionItem.canBeRefunded,
                  canBeCancelled: fareConditionItem.canBeCancelled
                };
              });
      }

      row[columnKey] = Array.isArray(aftersalesConditionsToDisplay) ? (
        <FareContainer>
          {aftersalesConditionsToDisplay.map(({ text, active }, index) => (
            <FareDescription color={active === false ? 'red' : 'black'} key={`${text}--${afterSalesKey}--${index}`}>
              {text}
            </FareDescription>
          ))}
          {shouldDisplayNotPossibleFareConditionsMessage({ aftersalesConditionsToDisplay, afterSalesKey }) && (
            <FareDescription color={'black'}>
              {displayAfterSalesNotPossibleMessage({
                afterSalesKey,
                cannotBeCancelledAndRefunded: aftersalesConditionsToDisplay.some(
                  ({ canBeCancelled, canBeRefunded }) => !canBeCancelled && !canBeRefunded
                ),
                intl
              })}
            </FareDescription>
          )}
        </FareContainer>
      ) : (
        <FareContainer shouldBeCentered={type === undefined}>
          <FareDescription color={'black'}>{aftersalesConditionsToDisplay}</FareDescription>
        </FareContainer>
      );

      row.containsInactiveItems =
        Array.isArray(aftersalesConditionsToDisplay) && aftersalesConditionsToDisplay.some(({ active }) => !active);
    });

    return row;
  });

  return (
    <FareWrapper>
      {rows.some(({ containsInactiveItems }) => containsInactiveItems) && (
        <FareConditionsInactiveMessage data-testid="fareConditions-inactiveMessage">
          <FormattedMessage id="fareConditions.inactiveMessage" />
        </FareConditionsInactiveMessage>
      )}
      <StyledTable
        dataCy="fare-condition-table"
        dataTestId="fare-condition-table"
        rows={rows}
        columns={columns}
        headerHeight={40}
        headerBackground={colours.Greyscale.CharcoalGrey}
        alternateBackground={colours.Greyscale.SmokeGrey}
      />
    </FareWrapper>
  );
};

FareConditions.propTypes = {
  journey: legShape,
  intl: intlShape,
  selectedProduct: PropTypes.string,
  currency: PropTypes.string
};

export default FareConditions;
